import {
  type JSX,
  type ReactNode,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { type MenuPlacement, placeMenu } from './menu-placement';
import { cx } from './primitives';

/**
 * A compact trigger and the panel it opens - the shape a table's filter row
 * takes in smb-voice's tenant portal, built on this platform's own controls.
 *
 * The trigger is an ordinary small secondary button as wide as its label, so a
 * row of them reads "Periode - Appartement - Statut" and fits on one line
 * where the boxed label-plus-select filters did not. The panel is whatever the
 * caller puts in it - a list of choices, two date fields - and is placed the
 * way the action menu's list is: through a portal at fixed coordinates, so the
 * `overflow` of a table wrapper cannot clip it, measured once it is in the DOM
 * and moved before paint by `placeMenu` so it never lands past the bottom of
 * the window.
 *
 * What closes it: a click outside, Escape (which returns focus to the
 * trigger), Tab, and any scroll or resize - fixed coordinates go stale the
 * moment the page moves, and a panel drifting away from its trigger is worse
 * than one that asks to be opened again. A scroll INSIDE the panel is the
 * exception, for the same reason it is on the menu.
 *
 * Every string arrives already translated (PRD NFR-006).
 */

export interface PopoverProps {
  /** Accessible name of the trigger AND of the panel: the noun this is about. */
  readonly label: string;
  /** What the trigger reads. */
  readonly trigger: ReactNode;
  /** Drawn as carrying a value - a filter that is narrowing something. */
  readonly active?: boolean;
  /** The panel's content, given the way to close it. */
  readonly children: (close: () => void) => ReactNode;
  readonly className?: string;
}

export function Popover({
  label,
  trigger,
  active = false,
  children,
  className,
}: PopoverProps): JSX.Element {
  const panelId = useId();
  const [open, setOpen] = useState(false);
  const [at, setAt] = useState<MenuPlacement>({ top: 0, left: 0, maxHeight: null });
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const placedRef = useRef(false);

  const close = useCallback((returnFocus: boolean) => {
    setOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent): void => {
      const target = event.target as Node;
      if (panelRef.current?.contains(target) === true) return;
      if (triggerRef.current?.contains(target) === true) return;
      close(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open, close]);

  useEffect(() => {
    if (!open) return;
    const onScroll = (event: Event): void => {
      const target = event.target;
      if (target instanceof Node && panelRef.current?.contains(target) === true) return;
      close(false);
    };
    const onResize = (): void => close(false);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [open, close]);

  /*
   * Measure and place before the first paint, once per opening (the menu's
   * rule, for the menu's reason), then hand focus to the first thing in the
   * panel a hand can use - a search field, the chosen entry, a date - so the
   * keyboard is already inside it.
   */
  useLayoutEffect(() => {
    if (!open) {
      placedRef.current = false;
      return;
    }
    if (placedRef.current) return;
    const anchor = triggerRef.current;
    const panel = panelRef.current;
    if (anchor === null || panel === null) return;
    placedRef.current = true;

    const anchorRect = anchor.getBoundingClientRect();
    const panelRect = panel.getBoundingClientRect();
    setAt((current) =>
      placeMenu({
        anchorTop: anchorRect.top,
        anchorBottom: anchorRect.bottom,
        listLeft: panelRect.left,
        listWidth: panelRect.width,
        listHeight: panelRect.height,
        inlineLeft: current.left,
        viewportWidth: window.innerWidth,
        viewportHeight: window.innerHeight,
      }),
    );
    const first = panel.querySelector<HTMLElement>(
      '[data-autofocus], input, select, textarea, button',
    );
    (first ?? panel).focus();
  }, [open]);

  const openPanel = (): void => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect !== undefined) setAt({ top: rect.bottom + 4, left: rect.left, maxHeight: null });
    setOpen(true);
  };

  const onPanelKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'Escape') {
      event.preventDefault();
      close(true);
    } else if (event.key === 'Tab') {
      // Do not preventDefault: Tab leaves the panel AND moves on.
      close(false);
    }
  };

  return (
    <span className={cx('cib-popover', className)}>
      <button
        type="button"
        ref={triggerRef}
        className={cx(
          'cib-button cib-button--secondary cib-popover__trigger',
          active && 'cib-popover__trigger--active',
        )}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        aria-label={label}
        title={label}
        onClick={() => (open ? close(false) : openPanel())}
      >
        <span className="cib-button__label">{trigger}</span>
        <Chevron />
      </button>
      {open
        ? createPortal(
            <div
              id={panelId}
              ref={panelRef}
              role="dialog"
              aria-label={label}
              tabIndex={-1}
              className="cib-popover__panel"
              style={{
                top: at.top,
                left: at.left,
                ...(at.maxHeight === null
                  ? {}
                  : { maxHeight: at.maxHeight, overflowY: 'auto' as const }),
              }}
              onKeyDown={onPanelKeyDown}
            >
              {children(() => close(true))}
            </div>,
            document.body,
          )
        : null}
    </span>
  );
}

function Chevron(): ReactNode {
  return (
    <svg className="cib-popover__chevron" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <path
        d="M4 6l4 4 4-4"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
